import { formatRecoveryCode } from "@bt/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ClockIcon,
  CopyIcon,
  DownloadIcon,
  FingerprintIcon,
  KeyRoundIcon,
  LockIcon,
  ShieldAlertIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Field } from "@/components/app/field";
import { useSignOut } from "@/components/app/user-menu";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { encryptsGarages, hasPasskeys, safeNext } from "@/lib/account";
import { errorCode, errorMessage } from "@/lib/errors";
import { garage } from "@/lib/garage";
import { useTRPC, useTRPCClient } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { AuthCard } from "../account/account-app";
import { passkeyCancelled, pickPasskey, takePrf } from "../account/passkeys";
import { FormError } from "../account/sign-in";
import { SecondFactorField } from "../account/two-factor";

/**
 * Garage encryption in the web app (ADR 0022): the screens before a garage opens when its key
 * isn't open here, the recovery keys shown once, and where they are made again.
 */

/** A recovery key, shown once: copy it, download it, and say it is kept before going on. */
export function RecoveryKeyView({
  code,
  kind,
  onDone,
}: {
  code: string;
  kind: "account" | "garage";
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [kept, setKept] = useState(false);
  const shown = formatRecoveryCode(code);
  const fileName =
    kind === "garage" ? `garage-recovery-key-${garage ?? ""}.txt` : "account-recovery-key.txt";
  return (
    <div className="flex flex-col gap-4">
      <p
        className="rounded-lg bg-muted px-4 py-3 text-center font-mono text-lg font-semibold tracking-widest select-all"
        data-testid="recovery-key"
      >
        {/* Groups of four stay whole when the line wraps on a phone. */}
        {shown.split("-").map((group, index, groups) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: the groups of a key never move
          <span key={index} className="inline-block whitespace-nowrap">
            {group}
            {index < groups.length - 1 ? "-" : ""}
          </span>
        ))}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            navigator.clipboard
              .writeText(shown)
              .then(() => toast.success(t("keys.copied")))
              .catch(() => toast.error(t("keys.copyFailed")))
          }
        >
          <CopyIcon />
          {t("keys.copy")}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            const text = `${t(kind === "garage" ? "keys.garageKey" : "keys.accountKey")}\n${
              kind === "garage" ? `${garage ?? ""}\n` : ""
            }${window.location.host}\n\n${shown}\n`;
            const link = document.createElement("a");
            link.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
            link.download = fileName;
            link.click();
            URL.revokeObjectURL(link.href);
          }}
        >
          <DownloadIcon />
          {t("keys.download")}
        </Button>
      </div>
      <p className="flex items-start gap-2 rounded-lg border border-partial/40 bg-partial-bg px-3 py-2 text-sm text-partial-fg">
        <TriangleAlertIcon className="mt-0.5 size-4 shrink-0" />
        {t(kind === "garage" ? "keys.garageKeyWarning" : "keys.accountKeyWarning")}
      </p>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 size-4 accent-brand"
          checked={kept}
          onChange={(e) => setKept(e.target.checked)}
        />
        {t("keys.kept")}
      </label>
      <Button disabled={!kept} onClick={onDone}>
        {t("keys.done")}
      </Button>
    </div>
  );
}

/** A full-page card before the garage, for what has to happen first. */
function GateCard({
  icon: Icon,
  title,
  body,
  children,
}: {
  icon: typeof LockIcon;
  title: string;
  body: string;
  children: ReactNode;
}) {
  return (
    <main className="flex min-h-dvh items-start justify-center bg-background px-4 pt-[max(3rem,env(safe-area-inset-top))] pb-12 sm:items-center sm:pt-12">
      <Card className="flex w-full max-w-md flex-col gap-5 p-5 sm:p-6">
        <div className="flex flex-col gap-2">
          <Icon className="size-7 text-muted-foreground" aria-hidden />
          <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
          <p className="text-sm text-muted-foreground">{body}</p>
        </div>
        {children}
      </Card>
    </main>
  );
}

/** After unlocking or restoring, everything loads again with the key open. */
function useReopen() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries();
}

/**
 * Their key is locked here (ADR 0022): a new browser, cleared cookies, a passkey without PRF, or
 * a password set by a reset. The password opens it, a passkey with PRF, or the account recovery
 * key; with none of them they start over with a new key.
 */
export function UnlockScreen() {
  const { t } = useTranslation();
  const client = useTRPCClient();
  const reopen = useReopen();
  const signOut = useSignOut();
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needsRecovery, setNeedsRecovery] = useState(false);
  // Two-factor sign-in (ADR 0011): a password that is checked also needs the code.
  const [askCode, setAskCode] = useState(false);
  const [secondCode, setSecondCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [startingOver, setStartingOver] = useState(false);
  const trpc = useTRPC();
  const { data: status, isPending: statusPending } = useQuery(trpc.keys.status.queryOptions());
  const [newPassword, setNewPassword] = useState("");
  // Recovering after a password reset (ADR 0022): the screen says so, then asks only for what is
  // still missing. Signed in with the new password, the server has it, and only the recovery key
  // or a passkey is asked; signed in another way, the new password is asked here too.
  const reset = Boolean(status?.recovering) || status?.lock === "passwordReset" || needsRecovery;
  const askNewPassword = Boolean(status?.needsPassword) && !needsRecovery;
  const hasRecoveryKey = status?.recoveryKey !== false;

  const run = async (work: () => Promise<void>) => {
    setError(null);
    setPending(true);
    try {
      await work();
      await reopen();
    } catch (e) {
      // A password that doesn't open the key any more was set by a reset: the recovery key does.
      if (errorCode(e) === "GARAGE_LOCKED") {
        setNeedsRecovery(true);
      } else if (errorCode(e) === "TWO_FACTOR_REQUIRED" && !askCode) setAskCode(true);
      else setError(errorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  if (statusPending) return null;
  // Only the ways this account has (ADR 0022): a passkey only if one can open the key.
  const canPasskey = hasPasskeys() && (status?.passkeys.length ?? 0) > 0;
  const noWay = reset && !hasRecoveryKey && !canPasskey;
  const body = !reset
    ? t("keys.lockedBody")
    : noWay
      ? t("keys.resetNoWay")
      : askNewPassword
        ? hasRecoveryKey
          ? t("keys.recoverBoth")
          : t("keys.recoverPasskeyFirst")
        : hasRecoveryKey && canPasskey
          ? t("keys.resetEither")
          : hasRecoveryKey
            ? t("keys.resetBody")
            : t("keys.resetPasskeyOnly");

  const passkeyOption = canPasskey ? (
    <div className="flex flex-col gap-1.5">
      <Button
        type="button"
        variant="outline"
        disabled={pending}
        onClick={() =>
          run(async () => {
            const options = await client.account.passkeySignInOptions.mutate();
            let response: Awaited<ReturnType<typeof pickPasskey>>;
            try {
              response = await pickPasskey(options);
            } catch (e) {
              if (passkeyCancelled(e)) return;
              throw e;
            }
            const prf = takePrf(response);
            const result = await client.account.passkeySignIn.mutate({
              response: { ...response },
              prf,
            });
            if (result.locked) throw new Error(t("keys.passkeyNoPrf"));
          })
        }
      >
        <FingerprintIcon />
        {t("keys.unlockPasskey")}
      </Button>
      <p className="text-xs text-muted-foreground">{t("keys.passkeyHint")}</p>
    </div>
  ) : null;

  const recoveryOption = hasRecoveryKey ? (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (code && (!askNewPassword || newPassword)) {
          void run(() =>
            client.keys.unlock
              .mutate({
                recoveryKey: code,
                newPassword: askNewPassword ? newPassword : null,
                code: askCode ? secondCode : null,
              })
              .then(() => {}),
          );
        }
      }}
    >
      {askNewPassword ? (
        <Field label={t("keys.adoptPassword")} htmlFor="unlock-new-password">
          <Input
            id="unlock-new-password"
            type="password"
            autoFocus
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </Field>
      ) : null}
      {askNewPassword && askCode ? (
        <SecondFactorField id="unlock-new-code" value={secondCode} onChange={setSecondCode} />
      ) : null}
      <Field label={t("keys.accountKey")} htmlFor="unlock-recovery" hint={t("keys.accountKeyHint")}>
        <Input
          id="unlock-recovery"
          autoComplete="off"
          spellCheck={false}
          className="font-mono uppercase"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </Field>
      <Button
        type="submit"
        variant={reset ? "default" : "outline"}
        disabled={pending || !code || (askNewPassword && !newPassword)}
      >
        <KeyRoundIcon />
        {t("keys.unlockRecovery")}
      </Button>
    </form>
  ) : null;

  return (
    <GateCard
      icon={LockIcon}
      title={reset ? t("keys.recoverTitle") : t("keys.lockedTitle")}
      body={body}
    >
      {!reset ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (password) {
              void run(() =>
                client.keys.unlock
                  .mutate({ password, code: askCode ? secondCode : null })
                  .then(() => {}),
              );
            }
          }}
        >
          <Field label={t("account.password")} htmlFor="unlock-password">
            <Input
              id="unlock-password"
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          {askCode ? (
            <SecondFactorField id="unlock-code" value={secondCode} onChange={setSecondCode} />
          ) : null}
          <Button type="submit" disabled={pending || !password}>
            {t("keys.unlock")}
          </Button>
        </form>
      ) : null}
      {/* The other ways, each said to be enough on its own. */}
      {[
        !reset ? "password" : null,
        recoveryOption ? "recovery" : null,
        passkeyOption ? "passkey" : null,
      ]
        .filter((option): option is string => option !== null)
        .map((option, index) => (
          <div key={option} className="flex flex-col gap-3">
            {index > 0 ? <OrDivider /> : null}
            {option === "recovery" ? recoveryOption : option === "passkey" ? passkeyOption : null}
          </div>
        ))}
      {noWay ? (
        <Button onClick={() => setStartingOver(true)}>{t("keys.startOverConfirm")}</Button>
      ) : null}
      <FormError message={error} />
      <div className="flex flex-wrap justify-between gap-2 border-t border-border pt-4">
        {noWay ? (
          <span />
        ) : (
          <Button variant="ghost" size="sm" onClick={() => setStartingOver(true)}>
            {t("keys.startOver")}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => void signOut()}>
          {t("account.signOut")}
        </Button>
      </div>
      <StartOverDialog
        open={startingOver}
        onOpenChange={setStartingOver}
        afterReset={status?.lock === "passwordReset"}
      />
    </GateCard>
  );
}

/** "or" between two ways of doing the same thing, either of which is enough. */
function OrDivider() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground" aria-hidden>
      <span className="h-px flex-1 bg-border" />
      {t("keys.or")}
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

/**
 * "Start over" (ADR 0022): where the user says they have no way back, so the system makes them a
 * new key. Right after a reset the new password is already known, so none is typed; elsewhere it
 * confirms. Then straight on to a new recovery key; their garage admins get an email meanwhile.
 */
function StartOverDialog({
  open,
  onOpenChange,
  afterReset,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  afterReset: boolean;
}) {
  const { t } = useTranslation();
  const client = useTRPCClient();
  const reopen = useReopen();
  const [password, setPassword] = useState("");
  const [askCode, setAskCode] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // After starting over: how many garage admins were emailed (none where they're the only one).
  const [done, setDone] = useState<{ notified: number } | null>(null);
  const finish = () => {
    onOpenChange(false);
    void reopen();
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : done ? finish() : onOpenChange(false))}
    >
      <DialogContent>
        {done ? (
          <>
            <DialogHeader
              title={t("keys.newKeyTitle")}
              description={done.notified ? t("keys.newKeyBody") : t("keys.newKeyBodyAlone")}
            />
            <DialogBody>
              <AccountRecoveryKeyMaker onDone={finish}>
                <Button variant="outline" size="lg" onClick={finish}>
                  {t("keys.notNow")}
                </Button>
              </AccountRecoveryKeyMaker>
            </DialogBody>
          </>
        ) : (
          <>
            <DialogHeader title={t("keys.startOverTitle")} description={t("keys.startOverBody")} />
            <form
              className="contents"
              onSubmit={async (event) => {
                event.preventDefault();
                setPending(true);
                setError(null);
                try {
                  const result = await client.keys.startOver.mutate({
                    password: afterReset ? null : password,
                    code: askCode ? code : null,
                  });
                  setDone({ notified: result.notified });
                } catch (e) {
                  if (errorCode(e) === "TWO_FACTOR_REQUIRED" && !askCode) setAskCode(true);
                  else setError(errorMessage(e, t));
                } finally {
                  setPending(false);
                }
              }}
            >
              <DialogBody className="flex flex-col gap-4">
                {afterReset ? null : (
                  <Field label={t("account.deletePassword")} htmlFor="start-over-password">
                    <Input
                      id="start-over-password"
                      type="password"
                      autoFocus
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </Field>
                )}
                {askCode ? (
                  <SecondFactorField id="start-over-code" value={code} onChange={setCode} />
                ) : null}
                <p className="text-xs text-muted-foreground">{t("keys.startOverOldKey")}</p>
                <FormError message={error} />
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={pending || (!afterReset && !password)}
                >
                  {t("keys.startOverConfirm")}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The garage has a key they don't hold: they started over, or joined by an invitation that
 * couldn't carry it. The garage recovery key gives it back; else a garage admin invites them again.
 */
export function NoKeyScreen({
  owed = false,
  restorable = false,
}: {
  owed?: boolean;
  restorable?: boolean;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const reopen = useReopen();
  const signOut = useSignOut();
  const restore = useMutation({
    ...trpc.garageKeys.restore.mutationOptions(),
    meta: { silent: true },
  });
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  return (
    <GateCard
      icon={owed ? ClockIcon : ShieldAlertIcon}
      title={t(owed ? "keys.owedTitle" : "keys.noKeyTitle", { name: garage })}
      body={
        owed ? t("keys.owedBody") : restorable ? t("keys.noKeyBody") : t("keys.noKeyOnlyRecovery")
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={async (event) => {
          event.preventDefault();
          setError(null);
          try {
            await restore.mutateAsync({ code });
            await reopen();
          } catch (e) {
            setError(errorMessage(e, t));
          }
        }}
      >
        <Field label={t("keys.garageKey")} htmlFor="restore-code">
          <Input
            id="restore-code"
            autoComplete="off"
            spellCheck={false}
            className="font-mono uppercase"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </Field>
        <FormError message={error} />
        <Button type="submit" disabled={restore.isPending || !code}>
          <KeyRoundIcon />
          {t("keys.restore")}
        </Button>
      </form>
      <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-4">
        <Button asChild variant="ghost" size="sm">
          <a href="/">{t("keys.otherGarages")}</a>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => void signOut()}>
          {t("account.signOut")}
        </Button>
      </div>
    </GateCard>
  );
}

/** Makes a garage recovery key and shows it once; from the banner and the garage settings. */
function GarageRecoveryKeyDialog({
  open,
  onOpenChange,
  replace,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  replace: boolean;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const create = useMutation({
    ...trpc.garageKeys.createRecoveryKey.mutationOptions(),
    meta: { silent: true },
  });
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const close = () => {
    setCode(null);
    setError(null);
    onOpenChange(false);
    void queryClient.invalidateQueries({ queryKey: trpc.garages.access.queryKey() });
  };
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader
          title={t("keys.garageKeyTitle")}
          description={t(replace ? "keys.garageKeyReplaceBody" : "keys.garageKeyBody")}
        />
        <DialogBody className="flex flex-col gap-4">
          {code ? (
            <RecoveryKeyView code={code} kind="garage" onDone={close} />
          ) : (
            <>
              <FormError message={error} />
              <Button
                disabled={create.isPending}
                onClick={async () => {
                  setError(null);
                  try {
                    setCode((await create.mutateAsync()).code);
                  } catch (e) {
                    setError(errorMessage(e, t));
                  }
                }}
              >
                <KeyRoundIcon />
                {t(replace ? "keys.garageKeyReplace" : "keys.garageKeyCreate")}
              </Button>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/**
 * For garage admins while the garage has no recovery key: a note above the page, not a wall. It
 * stays mounted, so its dialog still shows the new key once the note itself is gone.
 */
export function GarageRecoveryKeyBanner({ due }: { due: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      {due ? (
        <div className="flex flex-col gap-2 border-b border-partial/40 bg-partial-bg px-4 py-2.5 text-sm text-partial-fg sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-start gap-2">
            <KeyRoundIcon className="mt-0.5 size-4 shrink-0" />
            {t("keys.garageKeyDue")}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="self-start sm:self-auto"
            onClick={() => setOpen(true)}
          >
            {t("keys.garageKeyCreate")}
          </Button>
        </div>
      ) : null}
      <GarageRecoveryKeyDialog open={open} onOpenChange={setOpen} replace={false} />
    </>
  );
}

/** Garage settings: replace the garage recovery key (garage admins, where garages are encrypted). */
export function GarageRecoveryKeyCard() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!encryptsGarages()) return null;
  return (
    <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{t("keys.garageKey")}</p>
        <p className="text-sm text-muted-foreground">{t("keys.garageKeySettings")}</p>
      </div>
      <Button variant="outline" className="self-start sm:self-auto" onClick={() => setOpen(true)}>
        <KeyRoundIcon />
        {t("keys.garageKeyReplace")}
      </Button>
      <GarageRecoveryKeyDialog open={open} onOpenChange={setOpen} replace />
    </Card>
  );
}

/** Makes an account recovery key and shows it once; the welcome step and the profile. */
export function AccountRecoveryKeyMaker({
  onDone,
  children,
}: {
  onDone: () => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const client = useTRPCClient();
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  if (code) return <RecoveryKeyView code={code} kind="account" onDone={onDone} />;
  return (
    <div className="flex flex-col gap-2">
      <FormError message={error} />
      <Button
        size="lg"
        disabled={pending}
        onClick={async () => {
          setError(null);
          setPending(true);
          try {
            setCode((await client.keys.createRecoveryKey.mutate()).code);
          } catch (e) {
            setError(errorMessage(e, t));
          } finally {
            setPending(false);
          }
        }}
      >
        <KeyRoundIcon />
        {t("keys.accountKeyCreate")}
      </Button>
      {children}
    </div>
  );
}

/** Profile: the account recovery key, made or replaced. */
export function AccountRecoveryKeyCard() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { data: status, refetch } = useQuery(trpc.keys.status.queryOptions());
  const [open, setOpen] = useState(false);
  if (!status?.encrypts) return null;
  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <p className="min-w-0 flex-1 text-sm text-muted-foreground">
          {status.locked
            ? t("keys.profileLocked")
            : status.recoveryKey
              ? t("keys.profileHasKey")
              : status.passkeys.length
                ? t("keys.profileNoKeyPasskey")
                : t("keys.profileNoKey")}
        </p>
        <Button
          variant="outline"
          className="self-start sm:self-auto"
          disabled={status.locked}
          onClick={() => setOpen(true)}
        >
          <KeyRoundIcon />
          {status.recoveryKey ? t("keys.accountKeyReplace") : t("keys.accountKeyCreate")}
        </Button>
      </div>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) void refetch();
        }}
      >
        <DialogContent>
          <DialogHeader title={t("keys.accountKeyTitle")} description={t("keys.accountKeyBody")} />
          <DialogBody>
            {open ? (
              <AccountRecoveryKeyMaker
                onDone={() => {
                  setOpen(false);
                  void refetch();
                }}
              />
            ) : null}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * /account/recovery-key: the one-time offer of an account recovery key (ADR 0022), after the
 * welcome steps and at the first sign-in where garages are encrypted. Either answer is final;
 * the profile makes one later. Then on to ?next=.
 */
export function RecoveryKeyOfferPage() {
  const { t } = useTranslation();
  const client = useTRPCClient();
  const trpc = useTRPC();
  const { data: status, isPending } = useQuery(trpc.keys.status.queryOptions());
  const next = safeNext(new URLSearchParams(window.location.search).get("next"));
  const [pending, setPending] = useState(false);
  const due = Boolean(status?.recoveryKeyOffer);
  useEffect(() => {
    if (!isPending && !due) window.location.replace(next);
  }, [isPending, due, next]);
  if (isPending || !due) return null;
  return (
    <AuthCard
      title={t("keys.offerTitle")}
      body={status?.passkeys.length ? t("keys.offerBodyPasskey") : t("keys.offerBody")}
    >
      <AccountRecoveryKeyMaker onDone={() => window.location.assign(next)}>
        <Button
          variant="outline"
          size="lg"
          disabled={pending}
          onClick={async () => {
            setPending(true);
            await client.keys.dismissRecoveryOffer.mutate().catch(() => {});
            window.location.assign(next);
          }}
        >
          {t("keys.notNow")}
        </Button>
        <p className="text-center text-xs text-muted-foreground">{t("keys.later")}</p>
      </AccountRecoveryKeyMaker>
    </AuthCard>
  );
}

/** Sends someone to the offer once, from a garage that opened, while it is due. */
export function useRecoveryKeyOffer(open: boolean): void {
  const trpc = useTRPC();
  const { data: status } = useQuery({
    ...trpc.keys.status.queryOptions(),
    enabled: encryptsGarages() && open,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const due = open && Boolean(status?.recoveryKeyOffer);
  useEffect(() => {
    if (!due) return;
    const here = window.location.pathname + window.location.search;
    window.location.replace(`/account/recovery-key?next=${encodeURIComponent(here)}`);
  }, [due]);
}

/**
 * Recovering after a password reset, with the key already open (a passkey signed in): its
 * password lock still fits only the old password, and the server never saw the new one. Entered
 * once and checked, it takes over, so password sign-ins open the data by themselves (ADR 0022). A
 * wrong one warns that someone else may have changed it. "Skip for now" asks again next session.
 */
export function NewPasswordScreen({ onSkip }: { onSkip: () => void }) {
  const { t } = useTranslation();
  const client = useTRPCClient();
  const reopen = useReopen();
  const [password, setPassword] = useState("");
  const [askCode, setAskCode] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  return (
    <GateCard icon={KeyRoundIcon} title={t("keys.recoverTitle")} body={t("keys.adoptBody")}>
      <form
        className="flex flex-col gap-3"
        onSubmit={async (event) => {
          event.preventDefault();
          setError(null);
          setPending(true);
          try {
            await client.keys.adoptPassword.mutate({ password, code: askCode ? code : null });
            toast.success(t("keys.adopted"));
            await reopen();
          } catch (e) {
            if (errorCode(e) === "TWO_FACTOR_REQUIRED" && !askCode) setAskCode(true);
            else if (errorCode(e) === "WRONG_PASSWORD") setError(t("keys.adoptWrong"));
            else setError(errorMessage(e, t));
          } finally {
            setPending(false);
          }
        }}
      >
        <Field label={t("keys.adoptPassword")} htmlFor="adopt-password">
          <Input
            id="adopt-password"
            type="password"
            autoFocus
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {askCode ? <SecondFactorField id="adopt-code" value={code} onChange={setCode} /> : null}
        <FormError message={error} />
        <Button type="submit" disabled={pending || !password}>
          {t("common.save")}
        </Button>
      </form>
      <div className="flex justify-end border-t border-border pt-4">
        <Button variant="ghost" size="sm" onClick={onSkip}>
          {t("keys.skip")}
        </Button>
      </div>
    </GateCard>
  );
}

/** Whether "Skip for now" was chosen in this browser session. */
export function useNewPasswordSkip(): [boolean, () => void] {
  const [skipped, setSkipped] = useState(
    () => sessionStorage.getItem("bt.newPasswordSkip") === "1",
  );
  return [
    skipped,
    () => {
      sessionStorage.setItem("bt.newPasswordSkip", "1");
      setSkipped(true);
    },
  ];
}
